import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Input, Text } from 'rizzui';
import { Loader } from '../../../../../components/loader/SpinnerLogo';
import { ShowPermissionAccordion } from '../../../../../components/ui/PermissionsAccordion';
import useFetch from '../../../../../hooks/useFetch';
import FormGroup from '../../../../../layout/shared/form-group';
import { PermissionService } from '../../../../../services/auth/permissionService';
import { UserService } from '../../../../../services/user/userService';
import { Permission, PermissionGroup, PermissionsApiResponse, RolePermission } from '../../../../../type/entities/role-type';
import { UserApiResponse } from '../../../../../type/entities/user-type';
import cn from '../../../../../utils/classNames';
import { filterPermissionGroups } from '../../../../../utils/filterPermissionGroups';
import UserProfileLayout from '../../UserProfileLayout';

const UserPermissions = () => {

    const { id = '' } = useParams<{ id: string }>();
    const [selectedPermissions, setSelectedPermissions] = useState<number[]>([]);
    const [initialPermissionsCount, setInitialPermissionsCount] = useState(0);
    const [groupSearch, setGroupSearch] = useState<string>('');
    const [permissionSearch, setPermissionSearch] = useState<string>('');

    const [data] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await (new UserService()).getUserById(id);
        return response.getResponseData() as UserApiResponse;
    }, [id]));

    const [permissionsData, permissionsLoading] = useFetch(useCallback(async () => {
        const response = await (new PermissionService()).getPermissions();
        return response.getResponseData() as PermissionsApiResponse;
    }, []));

    const filteredPermissionsData = useMemo(() => {
        if (!permissionsData) return null;
        return filterPermissionGroups(permissionsData, groupSearch, permissionSearch);
    }, [permissionsData, groupSearch, permissionSearch]);

    const totalPermissions = useMemo(() => {
        return permissionsData?.reduce((acc: any, group: any) => acc + group.permissions.length, 0) || 0;
    }, [permissionsData]);

    useEffect(() => {
        if (data) {
            setSelectedPermissions(data?.userPermissions.map((permission: RolePermission) => permission.permission.id));
            setInitialPermissionsCount(data.userPermissions.length);
        }
    }, [data]);

    return (
        <UserProfileLayout>
            <div className="@container">
                <div>
                    <div className="mt-6 mb-4 flex flex-wrap items-center justify-between gap-4 p-4 bg-gray-50 rounded-lg shadow-sm">
                        <div className='flex flex-wrap items-center gap-6'>
                            <div className='flex items-center'>
                                <Text className="rizzui-input-label block text-base font-semibold me-3 text-gray-700">Buscar Grupo:</Text>
                                <Input
                                    id="groupSearch"
                                    value={groupSearch}
                                    onChange={(e) => setGroupSearch(e.target.value)}
                                    placeholder="Escribe para filtrar..."
                                    className="w-64"
                                />
                            </div>
                            <div className='flex items-center'>
                                <Text className="rizzui-input-label block text-base font-semibold me-3 text-gray-700">Buscar Permiso:</Text>
                                <Input
                                    id="permissionSearch"
                                    value={permissionSearch}
                                    onChange={(e) => setPermissionSearch(e.target.value)}
                                    placeholder="Escribe para filtrar..."
                                    className="w-64"
                                />
                            </div>
                        </div>
                        <div className='text-right'>
                            <div className="flex items-center justify-end gap-4">
                                <div className="text-center">
                                    <Text className="font-bold text-lg text-blue-600">{initialPermissionsCount}</Text>
                                    <Text className="text-xs text-gray-500">Actuales</Text>
                                </div>
                                <div className="text-center">
                                    <Text className="font-bold text-lg text-gray-800">{totalPermissions}</Text>
                                    <Text className="text-xs text-gray-500">Totales</Text>
                                </div>
                            </div>
                        </div>
                    </div>
                    {(filteredPermissionsData && !permissionsLoading)
                        ? filteredPermissionsData.map((permissionGroup: PermissionGroup) => {
                            const atLeastOneSelected = permissionGroup.permissions.some((permission: Permission) => selectedPermissions.includes(permission.id));

                            return (
                                <FormGroup
                                    key={`index-${permissionGroup.id}`}
                                    title={permissionGroup.label}
                                    //description='Lista de permisos asignados al usuario'
                                    className='pt-6 mt-6 border-t border-gray-200'
                                    childClassName='col-span-full grid gap-4 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 @4xl:col-span-8 @4xl:gap-5 xl:gap-7'
                                >
                                    <div key={permissionGroup.id} className={cn([atLeastOneSelected ? '' : 'hidden absolute', "col-span-full"])}>
                                        <ShowPermissionAccordion
                                            group={permissionGroup}
                                            selectAll={[]}
                                            selectedPermissions={selectedPermissions}
                                        />
                                    </div>
                                </FormGroup>
                            )
                        })
                        : <Loader height='50vh' />
                    }
                </div>
            </div>
        </UserProfileLayout>
    );
};

export default UserPermissions;